// Hook to resolve dynamic event slugs from Firestore
import { useState, useEffect } from "react";
import {
    getFirestore,
    collection,
    doc,
    getDoc,
    query,
    where,
    getDocs,
    limit,
} from "firebase/firestore";

// Slug → event id, remembered on this device so event pages resolve without
// signal (timing officers at water stations). Only used when the live lookup
// fails; a successful lookup always refreshes it.
const RESOLVED_KEY = "vx_event_ids";
const rememberEventId = (slug, id) => {
    try {
        const all = JSON.parse(localStorage.getItem(RESOLVED_KEY) || "{}");
        all[slug.trim().toLowerCase()] = id;
        localStorage.setItem(RESOLVED_KEY, JSON.stringify(all));
    } catch { /* storage blocked */ }
};
const rememberedEventId = (slug) => {
    try { return JSON.parse(localStorage.getItem(RESOLVED_KEY) || "{}")[slug.trim().toLowerCase()] || null; } catch { return null; }
};

export const useEventResolver = (slug) => {
    const [realEventId, setRealEventId] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!slug) {
            setRealEventId(null);
            setLoading(false);
            return;
        }

        let isActive = true;

        const resolveSlug = async () => {
            setLoading(true);
            setError(null);

            // Straight from Firestore, with the backend's rules (resolve-event
            // in routes/timing.js): an internal event ID still resolves, then
            // the normalised slug, and a slug two events share is an error.
            //
            // This used to ask the backend first. In standby the backend
            // sleeps, so the first visitor to any event page waited ~10s on a
            // cold start before anything rendered; the event_timing documents
            // are public reads, so the browser can do the same lookup itself.
            // Offline: Firestore would wait ~10 s before failing; answer from memory.
            if (typeof navigator !== "undefined" && navigator.onLine === false) {
                const known = rememberedEventId(slug);
                if (known) {
                    if (isActive) { setRealEventId(known); setLoading(false); }
                    return;
                }
            }
            try {
                const db = getFirestore();
                const requestedKey = slug.trim().toLowerCase();
                const directSnapshot = await getDoc(doc(db, "event_timing", requestedKey));
                if (directSnapshot.exists()) {
                    rememberEventId(slug, directSnapshot.id);
                    if (isActive) setRealEventId(directSnapshot.id);
                    return;
                }

                const normalizedSlug = requestedKey.replace(/[^a-z0-9]/g, "");
                const querySnapshot = await getDocs(
                    query(collection(db, "event_timing"), where("slug", "==", normalizedSlug), limit(2))
                );
                if (querySnapshot.empty) {
                    throw new Error(`Event not found for slug "${slug}"`);
                }
                if (querySnapshot.size > 1) {
                    throw new Error(`Event slug "${normalizedSlug}" is not unique`);
                }
                rememberEventId(slug, querySnapshot.docs[0].id);
                if (isActive) setRealEventId(querySnapshot.docs[0].id);
            } catch (err) {
                console.error("Failed to resolve slug:", err);
                const known = rememberedEventId(slug);
                if (known && isActive) {
                    setRealEventId(known);
                    return;
                }
                if (isActive) {
                    setError(err);
                    setRealEventId(null);
                }
            } finally {
                if (isActive) setLoading(false);
            }
        };

        resolveSlug();
        return () => {
            isActive = false;
        };
    }, [slug]);

    return { realEventId, loading, error };
};
